if (!customElements.get('recommend-package-products')) {
  class RecommendPackageProducts extends HTMLElement {
    constructor() {
      super();

      this.addToCartBtn = this.querySelector('.js-addtocart');
      this.addToCartText = this.querySelector('.js-addtocart-text');
      this.textError = this.querySelector('.js-error-message');
      this.controls = this.querySelector('.js-recommend-controls');
      this.mainProduct = document.querySelector('product-page');

      this.init();
    }

    init() {
      this.updateProductAddOns();
      this.onSelectVariant();
      this.onAddToCart();
      this.waitForMainProduct();
      this.listenVariantChanges();
    }

    // Wait for main product initialization
    waitForMainProduct() {
      const check = () => {
        if (this.mainProduct?.currentVariant || this.mainProduct?.variantData) {
          this.syncVariants();
        } else {
          requestAnimationFrame(check);
        }
      };
      check();
    }

    // Listen for main product variant changes
    listenVariantChanges() {
      requirejs(['pubsub'], () => {
        this.variantChangeSubscription = window.subscribeEvent(
          window.PUB_SUB_EVENTS.variantChange,
          ({ source }) => {
            if (source === 'product-form') {
              requestAnimationFrame(() => this.syncVariants());
            }
          }
        );
      });
    }

    // Sync recommend products variants with main product
    syncVariants() {
      const mainVariant = this.mainProduct?.currentVariant;
      if (!mainVariant) return;

      const mainOptions = mainVariant.options || [];
      const productItems = this.querySelectorAll('.js-product-item');

      productItems.forEach((item) => {
        const select = item.querySelector('.js-product-select');
        if (!select) return;

        const variants = JSON.parse(item.querySelector('.js-product-variants')?.textContent || '[]');
        if (!variants.length) return;

        // Find matching variant or fallback to first available
        const matched = variants.find(v => 
          v.available && v.options?.some(opt => mainOptions.includes(opt))
        ) || variants.find(v => v.available) || variants[0];

        if (matched && select.value != matched.id) {
          select.value = matched.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    updateProductAddOns() {
      if (!this.controls) return;

      const maxPage = Number(this.controls.dataset.maxPage);
      const controlButtons = this.controls.querySelectorAll('.js-recommend-control');
      const paginationList = this.querySelectorAll('.js-pagination-button');

      // Control buttons (prev/next)
      controlButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const currentIndex = Number(this.controls.dataset.current);
          const nextIndex = button.dataset.controls === 'next' 
            ? currentIndex + 1 
            : currentIndex - 1;

          this.navigateToPage(nextIndex, currentIndex, maxPage, paginationList);
        });
      });

      // Pagination buttons
      paginationList.forEach((button) => {
        button.addEventListener('click', () => {
          const currentIndex = Number(this.controls.dataset.current);
          const nextIndex = Number(button.dataset.page);

          this.navigateToPage(nextIndex, currentIndex, maxPage, paginationList);
        });
      });
    }

    // Navigate to specific page
    navigateToPage(nextIndex, currentIndex, maxPage, paginationList) {
      if (nextIndex < 1 || nextIndex > maxPage) return;

      this.controls.dataset.current = nextIndex;

      // Update pagination buttons
      paginationList.forEach((page) => page.classList.remove('active'));
      paginationList[nextIndex - 1]?.classList.add('active');

      // Update control buttons state
      const prevBtn = this.querySelector('.js-recommend-control[data-controls="prev"]');
      const nextBtn = this.querySelector('.js-recommend-control[data-controls="next"]');

      prevBtn?.classList.toggle('disabled', nextIndex === 1);
      nextBtn?.classList.toggle('disabled', nextIndex === maxPage);

      // Update lists visibility
      this.querySelector(`.js-recommend-list[data-index="${currentIndex}"]`)
        ?.classList.remove('is-open');
      this.querySelector(`.js-recommend-list[data-index="${nextIndex}"]`)
        ?.classList.add('is-open');
    }

    onSelectVariant() {
      const productInputs = this.querySelectorAll('.js-product-checkbox');

      productInputs.forEach((input) => {
        input.addEventListener('input', () => this.updateAddToCartText());
      });
    }

    // Update add to cart button text
    updateAddToCartText() {
      const checkedCount = this.querySelectorAll('.js-product-checkbox:checked').length;
      const { general, oneProduct, multipleProducts } = window.cartStrings.addToCart;

      this.addToCartText.innerHTML = 
        checkedCount === 0 ? general :
        checkedCount === 1 ? oneProduct :
        multipleProducts.replace('{{ quantity }}', checkedCount);
    }

    onAddToCart() {
      this.addToCartBtn.addEventListener('click', async () => {
        this.setLoadingState(true);

        try {
          // Get selected variants
          const selectedVariants = Array.from(
            this.querySelectorAll('.js-product-checkbox:checked')
          ).map(input => {
            const item = input.closest('.js-product-item');
            return item.querySelector('.js-product-select')?.value;
          }).filter(Boolean);

          // if (!selectedVariants.length) return;
          if (!selectedVariants.length) {
            this.setLoadingState(false);
            return;
          }

          // Add to cart
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: selectedVariants.map(id => ({ id, quantity: 1 })),
              sections: 'cart-drawer',
              sections_url: window.location.pathname
            })
          });

          const data = await response.json();

          if (data.status) {
            this.showError(data.description);
            return;
          }

          // Publish cart update event
          publishEvent(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            data
          });

        } catch (error) {
          console.error('Add to cart error:', error);
          this.showError('An error occurred. Please try again.');
        } finally {
          this.setLoadingState(false);
        }
      });
    }

    // Set loading state for add to cart button
    setLoadingState(isLoading) {
      this.addToCartBtn.classList.toggle('is-loading', isLoading);
      this.addToCartBtn.toggleAttribute('disabled', isLoading);
    }

    // Show error message
    showError(message) {
      this.textError.innerHTML = message;
      debounce(() => {
        this.textError.innerHTML = '';
      }, 4000)();
    }

    disconnectedCallback() {
      this.variantChangeSubscription?.();
    }
  }

  customElements.define('recommend-package-products', RecommendPackageProducts);
}